import { useNavStep } from '../../lib/NavHistory';

/*
 * ContactWorkspaceNavStep — registers the OPEN CONTACT as one global Back step.
 *
 * This is #65 Phase C3 for the CRM contact workspace: the step that Phase C2b-2
 * (#292) deliberately deferred rather than half-wire, and which was never
 * scheduled. Without it, the CRM's list→detail navigation is invisible to the
 * history stack: `setTab` and `openContactTab` are plain state, so
 * CRM → Contacts → contact → Back unwound the last ROUTE entry and left the CRM
 * entirely, landing on the user's dashboard.
 *
 * ⚠ RENDER IT KEYED ON THE ACTIVE CONTACT ID:
 *
 *     {activeId != null && (
 *       <ContactWorkspaceNavStep key={activeId} contactId={activeId} onClose={closeContactTab} />
 *     )}
 *
 * The `key` is load-bearing, not cosmetic. Because the workspace keeps several
 * contacts open at once (src/lib/workspaceTabs.js), the obvious
 * `active: selectedContactId != null` predicate is WRONG: opening a second
 * contact does not flip that boolean, so no second step is pushed, and after one
 * Back the user is left with a contact still open and no step behind it — the
 * next Back leaves the CRM. Same defect, one level deeper.
 *
 * Keying on the id makes switching or closing the active contact unmount this
 * component and mount a fresh one, so the stack always holds EXACTLY ONE step
 * that tracks whichever contact is currently active. The result is the intended
 * behaviour: one Back per open contact, each closing the ACTIVE tab — which is
 * precisely what the in-view "Back to List" button does, so the two controls
 * agree (acceptance criterion (a)).
 *
 * ⚠ `label` is a LITERAL STATIC STRING. The nav stack persists route entries to
 * sessionStorage, and #292 established that a computed label here would put a
 * patient name or an id into browser storage. 'Contact record' names the KIND of
 * thing, never the thing.
 */
export default function ContactWorkspaceNavStep({ contactId, onClose }) {
  useNavStep({
    active: contactId != null,
    label: 'Contact record',
    // The SAME handler the in-view Back button calls, so global Back, the
    // keyboard shortcuts, the history dropdown and the Capacitor hardware
    // button all land exactly where the button lands.
    onBack: () => { if (onClose) onClose(contactId); },
  });

  /*
   * ⛔⛔ THE BROWSER'S BACK BUTTON IS NOT WIRED HERE — AND MUST NOT BE.
   *
   * It briefly was, and that was wrong (review finding, Cowork, PR #620,
   * 10 Oct 2026). This component is KEYED ON THE ACTIVE CONTACT ID, which is
   * exactly right for the NavHistory step above — one Back step per open
   * contact — and exactly wrong for a browser history entry.
   *
   * ⚠⚠ Switching from contact A to contact B leaves BOTH open and only moves
   * `activeId` (src/lib/workspaceTabs.js `activateTab`), so this component
   * REMOUNTS. A browser step living here would then run A's cleanup
   * `history.go(-1)` — asynchronous — and B's `pushState` in the same commit,
   * and the queued traversal would land on B's entry and close the contact the
   * person had just opened.
   *
   * The browser entry therefore belongs to the WHOLE WORKSPACE, not to one
   * record in it, and is held by src/pages/CRMPage.js with
   * `active: selectedContactId != null`. See the invariant at the top of
   * src/lib/browserBackStep.js.
   */
  return null;
}
